import isEqual from 'lodash-es/isEqual';
import * as moment from 'moment';
import type { JSONFilterGroup, FilterCriteria } from '~/types/FilterGroup';
import * as order from '~/types/Order';

function parseFilterGroups(show: string, filterGroups: JSONFilterGroup[]) {
  if (!filterGroups) {
    return {};
  }

  const newFilters: Record<string, unknown> = {};

  const filterGroup = filterGroups.find((filterGroup) => filterGroup.name === show);
  if (filterGroup && filterGroup.filter_criteria) {
    filterGroup.filter_criteria.forEach((filterCriteria: FilterCriteria) => {
      const filter = filterCriteria.filter;
      if (filter.field === 'ORDER_BY') {
        newFilters['order_by'] = filter.value.text;
      }
      if (filter.field === 'SORT_BY') {
        newFilters['sort_by'] = filter.value.text;
      }
      if (filter.field === 'WEIGHT') {
        const comparator = filter.comparator === 'less_than' ? 'lt' : 'gt';
        newFilters[`weight_${comparator}`] = filter.value.value;
        newFilters['weight_unit'] = filter.value.enum;
      }
      if (filter.field === 'STORE_PLATFORM') {
        if (filter.value.set && filter.value.set.length > 0) {
          newFilters['shop_app'] = filter.value.set[0];
        }
      }
      if (filter.field === 'IS_INTERNATIONAL') {
        newFilters['is_international'] = filter.value.boolean?.toString();
      }
      if (filter.field === 'DATE_CREATED') {
        if (filter.comparator === 'greater_than_or_equal_to') {
          newFilters['start_date'] = moment(filter.value.date).format();
        } else if (filter.comparator === 'less_than_or_equal_to') {
          newFilters['end_date'] = moment(filter.value.date).format();
        }
      }
      if (filter.field === 'SHIPPING_METHOD') {
        newFilters['shipping_method'] = filter.value.set;
      }
      if (filter.field === 'ITEM') {
        newFilters['item'] = filter.value.set?.sort();
      }
      if (filter.field === 'ORDER_STATUS') {
        if (filter.value.set && filter.value.set.length > 0) {
          newFilters['order_status'] = filter.value.set.length > 1 ? 'pending' : 'fulfilled';
        }
      }
      if (filter.field === 'SEARCH_TEXT') {
        newFilters['q'] = filter.value.text;
      }
    });
  }

  return newFilters;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseFilters(filters: any): any[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filterCriteria: any[] = [];
  if ('weight_unit' in filters) {
    const weightFilter = {
      filter: {
        field: 'weight',
        value: {
          enum: filters.weight_unit,
        },
      },
    };
    if ('weight_lt' in filters) {
      filterCriteria.push({
        filter: {
          ...weightFilter.filter,
          value: {
            ...weightFilter.filter.value,
            value: filters.weight_lt,
          },
          comparator: 'less_than',
        },
      });
    }
    if ('weight_gt' in filters) {
      filterCriteria.push({
        filter: {
          ...weightFilter.filter,
          value: {
            ...weightFilter.filter.value,
            value: filters.weight_gt,
          },
          comparator: 'greater_than',
        },
      });
    }
  }

  if ('shop_app' in filters) {
    filterCriteria.push({
      filter: {
        field: 'store_platform',
        comparator: 'in',
        value: {
          set: [filters['shop_app']],
        },
      },
    });
  }

  if ('is_international' in filters) {
    filterCriteria.push({
      filter: {
        field: 'is_international',
        comparator: 'equal_to',
        value: {
          boolean: filters['is_international'] === 'true',
        },
      },
    });
  }

  if ('start_date' in filters) {
    filterCriteria.push({
      filter: {
        field: 'date_created',
        comparator: 'greater_than_or_equal_to',
        value: {
          date: filters['start_date'],
        },
      },
    });
  }

  if ('end_date' in filters) {
    filterCriteria.push({
      filter: {
        field: 'date_created',
        comparator: 'less_than_or_equal_to',
        value: {
          date: filters['end_date'],
        },
      },
    });
  }

  if ('shipping_method' in filters) {
    filterCriteria.push({
      filter: {
        field: 'shipping_method',
        comparator: 'in',
        value: {
          set: filters['shipping_method'],
        },
      },
    });
  }

  if ('item' in filters) {
    filterCriteria.push({
      filter: {
        field: 'item',
        comparator: 'in',
        value: {
          set: filters['item'],
        },
      },
    });
  }

  if ('order_status' in filters) {
    filterCriteria.push({
      filter: {
        value: {
          set:
            filters['order_status'] === 'pending'
              ? [
                  order.OrderStatus.PAID,
                  order.OrderStatus.UNKNOWN,
                  order.OrderStatus.AWAITPAY,
                  order.OrderStatus.PARTIALLY_FULFILLED,
                ]
              : [order.OrderStatus.SHIPPED],
        },
        field: 'ORDER_STATUS',
        comparator: 'in',
      },
    });
  }

  if ('q' in filters) {
    filterCriteria.push({
      filter: {
        field: 'search_text',
        comparator: 'equal_to',
        value: {
          text: filters['q'],
        },
      },
    });
  }
  if ('sort_by' in filters) {
    filterCriteria.push({
      filter: {
        field: 'sort_by',
        comparator: 'equal_to',
        value: {
          text: filters['sort_by'],
        },
      },
    });
  }
  if ('order_by' in filters) {
    filterCriteria.push({
      filter: {
        field: 'order_by',
        comparator: 'equal_to',
        value: {
          text: filters['order_by'],
        },
      },
    });
  }

  return filterCriteria;
}

function getTabName(
  nonEmptyFilterValues: string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: Record<string, any>,
  savedTabName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterGroups: any,
  // filterGroups: { name: string }[],
) {
  // TODO: This looks like a bug. The usage suggests it wants an array but it is always passed an object.
  const savedFilterGroup =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filterGroups.find((filterGroup: any) => filterGroup.name === savedTabName) || {};
  const parsedFilters = parseFilterGroups(savedTabName, [savedFilterGroup]);

  const cleanedFilters = Object.keys(filters).reduce((cleanFilters, filterName) => {
    if (filters[filterName]) {
      return {
        ...cleanFilters,
        [filterName]: filters[filterName],
      };
    }
    return cleanFilters;
  }, {});

  if (nonEmptyFilterValues.length === 0) return savedTabName;
  if (isEqual(cleanedFilters, parsedFilters)) return savedTabName;

  // We need to add another case here to check if the filters are changed due to pagination
  // When filters are changed due to pagination, we should not change the tab name
  // This is to fix this ticket - https://shippo.atlassian.net/browse/WEB-71
  // When we change page from the landing page to the next page, the tab name is changing to new
  // Because the of filter values `order_by=desc&sort_by=_score` is added to the URL
  // So we check for exactly those values and return the savedTabName which is the current tab name / undefined in this case
  if (
    nonEmptyFilterValues.length === 2 &&
    filters.order_by === 'desc' &&
    filters.sort_by === '_score'
  ) {
    return savedTabName;
  }

  return 'new';
}

/**
  @description remove SEARCH_TEXT filter query from filter_criteria list if value text is empty.
 */
function cleanFilterCriteria(filters: JSONFilterGroup[]) {
  return filters.map((filter) => {
    const removeEmptySearchText = filter.filter_criteria.filter(
      (criteria) =>
        criteria.filter.field !== 'SEARCH_TEXT' ||
        (criteria.filter.field === 'SEARCH_TEXT' && criteria.filter.value.text),
    );
    return { ...filter, filter_criteria: removeEmptySearchText };
  });
}

export { parseFilterGroups, parseFilters, getTabName, cleanFilterCriteria };
